import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";

const Investors = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />

      <main className="pt-24 bg-gradient-to-r from-primary/5 via-accent/5 to-secondary/5" role="main">
        <div className="max-w-4xl mx-auto px-4 py-16">
          {/* Hero */}
          <header
            className="text-center mb-10 relative overflow-hidden rounded-3xl"
            aria-labelledby="investors-title"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-accent/5 to-secondary/5" />
            <div className="relative py-12 px-6 sm:px-10">
              <h1
                id="investors-title"
                className="text-4xl sm:text-5xl font-display font-bold mb-6"
              >
                For{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Investors
                </span>
              </h1>

              <p className="text-xl text-primary font-medium mb-6 bg-gradient-to-r from-primary/10 to-accent/10 py-4 px-6 rounded-xl border border-primary/20 inline-block">
                Invest in the future of family finance.
              </p>

              <section
                className="mt-6 bg-gradient-to-br from-primary/5 to-accent/5 p-8 rounded-2xl border border-primary/15 text-left"
                aria-label="Investment opportunity summary"
              >
                <p className="text-lg leading-relaxed">
                  Flexaura is unlocking a multibillion-dollar opportunity across three core verticals: 
                  education, healthcare, and travel. These are high-value, high-frequency needs where 
                  families require support — and where BNPL is rapidly scaling.
                </p>
              </section>
            </div>
          </header>

          {/* Content */}
          <div className="prose prose-lg max-w-none space-y-10 prose-headings:font-display prose-headings:text-primary">
            
            {/* The opportunity */}
            <section className="text-center bg-gradient-to-br from-primary/5 to-accent/5 p-8 rounded-xl border border-primary/10" aria-labelledby="opportunity-heading">
              <h2 id="opportunity-heading" className="text-2xl font-semibold mb-6">
                The opportunity
              </h2>
              <div className="space-y-4 text-left max-w-3xl mx-auto">
                <div className="flex items-start space-x-4">
                  <div className="w-3 h-3 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg">GCC families spend billions annually on tuition, healthcare, and holidays.</p>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-3 h-3 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg">Demand for flexible payment options is growing faster than traditional finance can serve.</p>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-lg">Flexaura is the first marketplace to aggregate BNPL, banks, and finance houses under one roof.</p>
                </div>
              </div>
            </section>

            {/* Why us */}
            <section className="text-center bg-gradient-to-br from-primary/5 to-accent/5 p-8 rounded-xl border border-primary/10" aria-labelledby="why-heading">
              <h2 id="why-heading" className="text-2xl font-semibold mb-6">
                Why us
              </h2>
              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="p-6 rounded-xl border border-border bg-card">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-primary mb-2">Diversified model</h3>
                  <p className="text-muted-foreground">Transaction fees + referral commissions + SaaS vendor subscriptions.</p>
                </div>

                <div className="p-6 rounded-xl border border-border bg-card">
                  <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-accent mb-2">Early traction</h3>
                  <p className="text-muted-foreground">MVP launched with travel, healthcare, and education categories.</p>
                </div>

                <div className="p-6 rounded-xl border border-border bg-card">
                  <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-emerald-700 mb-2">Scalable roadmap</h3>
                  <p className="text-muted-foreground">Regional expansion, new verticals, and AI-driven credit matching.</p>
                </div>
              </div>
            </section>

            {/* Our ask */}
            <section className="text-center bg-gradient-to-br from-primary/5 to-accent/5 p-8 rounded-xl border border-primary/10" aria-labelledby="ask-heading">
              <h2 id="ask-heading" className="text-2xl font-semibold mb-4">
                Our ask
              </h2>
              <p className="text-lg leading-relaxed mb-6 max-w-2xl mx-auto">
                We are raising our first round to accelerate product development, expand our partner network, 
                and capture early market share.
              </p>
            </section>

            {/* CTA */}
            <section
              className="text-center bg-gradient-to-r from-primary/5 to-accent/5 p-8 rounded-xl border border-primary/10"
              aria-label="Contact and call to action"
            >
              <p className="text-lg font-medium text-primary mb-3">
                Ready to join the future of family finance?
              </p>
              <p className="text-lg font-semibold mb-6">
                Request our pitch deck and learn more about the opportunity.
              </p>

              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="mailto:hector@flexaura.me"
                  className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 transition"
                  aria-label="Email investors at Flexaura"
                >
                  <Mail className="w-4 h-4" />
                  Request pitch deck
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 border border-border bg-card hover:bg-accent/10 transition"
                  aria-label="Contact Flexaura"
                >
                  Schedule a call
                </Link>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Investors;